// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Sager as data: the case list's cards (R1), and the words for creating and
 * uploading a case. Every figure is read off a server response; this module
 * only words it.
 */

import { ApiRequestError, caseBaseUrl } from '../api/client';
import type { CaseSummary, ProjectInfo, SurveyFractions, SurveySummary } from '../api/types';
import { formatBytes } from '../app/cases';
import type { Tone } from '../kortlaegning/vocab';
import { danishDate, percentText } from '../overblik/model';

export interface CaseStatus {
  label: string;
  tone: Tone;
}

/**
 * A survey with nothing in it: no queued/approved/rejected types at all.
 * Shared by `caseStatus` (Kladde) and `cardProgress` (F7) so the two never
 * contradict each other — a survey whose types are all rejected (`all`
 * excludes `rejected`, so it reads 0) is not "no survey", it is done.
 */
function hasNoSurveyTypes(s: SurveySummary): boolean {
  return s.counts.all === 0 && s.counts.rejected === 0;
}

/**
 * The card's status pill (R3), a wording of server numbers. `f` is
 * `GET /survey/fractions`: `undefined` while loading, `null` when it failed.
 * Without it nothing can be called done, so it reads Gennemgang. Agrees with
 * Overblik/Indberetning's readiness rule (`f.ready`): a project with no
 * fraction rows is never "Klar til indberetning", since `ready` itself
 * requires at least one fraction row and no blockers — this function never
 * re-derives that, only reads it.
 */
export function caseStatus(s: SurveySummary, f: SurveyFractions | null | undefined): CaseStatus {
  if (hasNoSurveyTypes(s)) return { label: 'Kladde', tone: 'wait' };
  if (s.counts.queue > 0 || !f || f.blocking_types > 0) return { label: 'Gennemgang', tone: 'accent' };
  if (f.ready) return { label: 'Klar til indberetning', tone: 'good' };
  return { label: 'Gennemgået', tone: 'good' };
}

/** What the progress line says for a project with no survey types. */
export const NO_SURVEY_TEXT = 'Ingen kortlægning endnu';

/**
 * The card's progress ring (R2): approved of all types, the same figures as
 * the Bygning top bar's ring — or null when the project has no survey yet
 * (F7: the same "no types at all" test `caseStatus` uses for Kladde, so a
 * survey whose types are all rejected still shows its progress rather than
 * contradicting a Gennemgået pill with "Ingen kortlægning endnu").
 */
export function cardProgress(s: SurveySummary): { approved: number; total: number } | null {
  if (hasNoSurveyTypes(s)) return null;
  return { approved: s.counts.approved, total: s.counts.all };
}

/** The card's reuse share beside the ring, "54 % bevaring/genbrug"; null with no survey (as `cardProgress`). */
export function cardReuseText(s: SurveySummary): string | null {
  if (hasNoSurveyTypes(s)) return null;
  return `${s.reuse_share === null ? '—' : `${percentText(s.reuse_share)} %`} bevaring/genbrug`;
}

/** Address and organisation, whichever the record has (no bygherre is stored). */
export function cardSubline(p: ProjectInfo | undefined): string {
  const parts: string[] = [];
  const address = p?.building_address?.trim();
  if (address) parts.push(address);
  const org = p?.survey_organisation?.trim();
  if (org) parts.push(`udarbejdet af ${org}`);
  return parts.length > 0 ? parts.join(' · ') : 'Ingen adresse registreret';
}

/** The foot's date: the registration date in place of the prototype's Frist (not stored). */
export function cardDate(p: ProjectInfo | undefined): string {
  const date = p?.survey_date?.trim();
  return date ? `Registreret ${danishDate(date)}` : '—';
}

/** How to serve more cases: every `.rux` in a directory is one. */
export const SERVE_DIRECTORY_COMMAND = 'ruxd --local <mappe>';

/**
 * The card's heading: the building record's name when the project has one,
 * else the case's own name (the file stem, or what it was named on creation).
 */
export function cardTitle(c: CaseSummary, record: ProjectInfo | undefined): string {
  const recordName = record?.name?.trim();
  return recordName ? recordName : c.name;
}

/**
 * The small print under a card: what tells two copies of one scan apart. The
 * file name, or — for a case the server created or received, whose file is
 * always `project.rux` — the case's own name; then the size.
 */
export function cardFileLine(c: CaseSummary): string {
  const what = c.file_name === 'project.rux' ? c.name : c.file_name;
  return `${what} · ${formatBytes(c.size_bytes)}`;
}

/** Cases in list order: active ones first, then archived; each by name. */
export function sortCases(cases: readonly CaseSummary[]): CaseSummary[] {
  return [...cases].sort(
    (a, b) => Number(a.archived) - Number(b.archived) || a.name.localeCompare(b.name, 'da'),
  );
}

/** "37 %" for an upload in progress. */
export function uploadPercent(sent: number, total: number): string {
  if (!(total > 0)) return '0 %';
  return `${Math.min(100, Math.floor((sent / total) * 100))} %`;
}

/**
 * Why creating, uploading or deleting a case failed, said so the user can act
 * on it. Code-first (`cause.code`, ruxd-202): the 409 branch tries the code
 * before the `cause.url` check it replaces, so an older server (no `code`)
 * still gets the right line. `create`'s 409 ("no data dir") carries no code
 * of its own, so it falls through to the same check unaffected.
 */
export function caseWriteErrorText(cause: unknown, action: 'create' | 'upload' | 'delete'): string {
  if (action === 'delete') return caseDeleteErrorText(cause);
  if (cause instanceof ApiRequestError) {
    const { code } = cause;
    switch (cause.status) {
      case 413:
        return 'Filen er større, end serveren tager imod.';
      case 422:
        return 'Filen er ikke et ReUseX-projekt (.rux).';
      case 429:
        return 'Der er for mange uploads i gang. Prøv igen om lidt.';
      case 409:
        if (code === 'upload_offset_mismatch' || code === 'upload_incomplete')
          return 'Uploaden kom ud af trit med serveren. Start den forfra.';
        if (code === 'uploads_unavailable')
          return 'Serveren kan ikke modtage sager (den viser én fil). Start den med en mappe.';
        // Only refusing to START is about the server having no data dir; a
        // 409 on a chunk or on completing means the upload fell out of step.
        if (action === 'upload' && !/\/uploads$/.test(cause.url))
          return 'Uploaden kom ud af trit med serveren. Start den forfra.';
        return action === 'upload'
          ? 'Serveren kan ikke modtage sager (den viser én fil). Start den med en mappe.'
          : 'Serveren kan ikke oprette sager (den viser én fil). Start den med en mappe.';
      case 507:
        return 'Der er ikke plads nok på serveren til filen.';
      case 400:
        return 'Navnet kan ikke bruges. Skriv et navn uden kontroltegn.';
      default:
        break;
    }
  }
  if (cause instanceof DOMException && cause.name === 'AbortError') return 'Upload afbrudt.';
  return action === 'upload'
    ? 'Upload mislykkedes. Tjek forbindelsen og prøv igen.'
    : 'Sagen kunne ikke oprettes. Prøv igen.';
}

function caseDeleteErrorText(cause: unknown): string {
  if (cause instanceof ApiRequestError) {
    switch (cause.status) {
      case 409:
        return 'Sagen kan ikke slettes lige nu: et job kører eller venter, eller sagen ligger uden for serverens datamappe.';
      case 404:
        return 'Sagen findes ikke længere på serveren.';
      case 403:
        return 'Kun sagens ejer kan slette den.';
      default:
        break;
    }
  }
  return 'Sagen kunne ikke slettes. Prøv igen.';
}

/**
 * Whether a card offers "Slet": the server may delete the case (it lives in
 * the data dir) and the caller owns it. An older server omits `role`, and
 * local mode's implicit user owns every case, so a missing role counts as
 * owner; the server refuses anyone else anyway.
 */
export function canDeleteCase(c: CaseSummary): boolean {
  return c.deletable && (c.role == null || c.role === 'owner');
}

/** The card's delete button: "Slet", then "Bekræft: slet" once armed. */
export function deleteCaseLabel(armed: boolean): string {
  return armed ? 'Bekræft: slet' : 'Slet';
}

/** What an armed delete says will happen. */
export const DELETE_CASE_CONFIRM_TEXT = 'Sagen flyttes til serverens papirkurv. Klik igen for at slette.';

/**
 * A press on a card's "Slet" (two-click confirm): the first one arms that
 * case, a second on the same armed case removes it.
 */
export function deleteCaseClick(armed: string | null, cid: string): 'arm' | 'remove' {
  return armed === cid ? 'remove' : 'arm';
}

/**
 * Delete `cid` on the server (moved to its trash) and return the list
 * without it. A refusal (409 busy/not deletable, 404, 403) throws, and the
 * list is left as it was.
 */
export async function removeCase(
  api: { remove(cid: string): Promise<void> },
  cases: readonly CaseSummary[],
  cid: string,
): Promise<CaseSummary[]> {
  await api.remove(cid);
  return cases.filter((c) => c.id !== cid);
}

/** What a card shows, from the list's `summary` alone (no per-case request). */
export interface CardFigures {
  record: ProjectInfo | undefined;
  survey: SurveySummary | undefined;
  status: CaseStatus;
  /** The server could not read the project at all. */
  unreadable: boolean;
}

export function cardFigures(c: CaseSummary): CardFigures {
  const s = c.summary;
  if (!s) {
    return {
      record: undefined,
      survey: undefined,
      status: s === null ? { label: 'Kan ikke læses', tone: 'crit' } : { label: 'Ukendt', tone: 'wait' },
      unreadable: s === null,
    };
  }
  const record = s.project ?? undefined;
  if (!s.survey) {
    // An older project the server has not migrated yet: the record only.
    return { record, survey: undefined, status: { label: 'Åbn for status', tone: 'wait' }, unreadable: false };
  }
  return { record, survey: s.survey, status: caseStatus(s.survey, s.fractions), unreadable: false };
}

/** The card's plan thumbnail (lazy; cached by the server). */
export function caseThumbUrl(cid: string): string {
  return `${caseBaseUrl(cid)}/renders?view=plan&width=640&height=248`;
}
